import { createFileRoute, Link } from "@tanstack/react-router";
import { ChefHat, Heart, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/chi-siamo")({
  head: () => ({
    meta: [
      { title: "Chi siamo — ChefConnect Italia" },
      {
        name: "description",
        content:
          "ChefConnect è la piattaforma italiana che connette clienti e chef privati. Scopri la nostra missione, i valori e il team.",
      },
      { property: "og:title", content: "Chi siamo — ChefConnect Italia" },
      {
        property: "og:description",
        content:
          "La piattaforma italiana per trovare chef privati verificati. Missione, valori e team.",
      },
      { property: "og:url", content: "https://chefconnectitalia.com/chi-siamo" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://chefconnectitalia.com/chi-siamo" }],
  }),
  component: ChiSiamoPage,
});

function ChiSiamoPage() {
  return (
    <article className="container mx-auto max-w-3xl px-4 py-16 sm:py-24">
      <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">Chi siamo</p>
      <h1 className="mt-3 font-display text-5xl leading-tight sm:text-6xl">
        Chi ama cucinare,<br />
        <span className="italic text-gold">merita di essere trovato.</span>
      </h1>
      <p className="mt-6 text-lg text-muted-foreground">
        ChefConnect è il punto d'incontro tra chi ha talento ai fornelli e chi
        desidera vivere un'esperienza culinaria autentica a casa propria.
        Personal chef, cuochi, professionisti della ristorazione e appassionati:
        qui c'è spazio per chiunque sappia trasformare una cena in un ricordo.
      </p>

      <section className="mt-14">
        <h2 className="font-display text-3xl">Perché esistiamo</h2>
        <p className="mt-4 text-muted-foreground">
          In Italia ci sono migliaia di persone che sanno cucinare benissimo, ma
          non hanno un modo semplice per farsi conoscere. E ci sono altrettante
          famiglie che vorrebbero una cena speciale in casa senza dover
          chiamare dieci ristoranti. ChefConnect serve a fare incontrare gli
          uni con gli altri, senza intermediari opachi e senza commissioni
          nascoste: il rapporto resta diretto tra cliente e chef.
        </p>
      </section>

      <section className="mt-14">
        <h2 className="font-display text-3xl">A chi è rivolto</h2>
        <p className="mt-4 text-muted-foreground">
          Non bisogna avere una stella Michelin per iscriversi. ChefConnect è
          per chi cucina con cura: personal chef, cuochi a domicilio,
          professionisti della ristorazione e appassionati con esperienza.
          Quello che conta è la qualità del lavoro e il rispetto per chi siede
          a tavola.
        </p>
      </section>

      <section className="mt-14">
        <h2 className="font-display text-3xl">Come lavoriamo</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-3">
          {[
            {
              icon: ShieldCheck,
              title: "Fiducia",
              text: "Verifichiamo documenti, identità e qualifiche prima di assegnare il badge. Le recensioni potranno essere lasciate solo da chi ha davvero prenotato.",
            },
            {
              icon: Heart,
              title: "Semplicità",
              text: "Niente moduli infiniti, niente intermediari. Cerchi, contatti, prenoti. Il resto succede in cucina.",
            },
            {
              icon: Sparkles,
              title: "Autenticità",
              text: "Crediamo nella cucina vera: quella che racconta un territorio, una storia, una mano. Non quella fotografata per i social.",
            },
          ].map((v) => (
            <div key={v.title} className="rounded-xl border border-border bg-card p-5">
              <v.icon className="h-5 w-5 text-gold" />
              <h3 className="mt-3 font-display text-lg">{v.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="font-display text-3xl">Se ami cucinare</h2>
        <p className="mt-4 text-muted-foreground">
          ChefConnect è uno strumento per trasformare la tua passione in
          un'opportunità concreta: profilo pubblico, visibilità nei motori di
          ricerca, richieste dei clienti gestite in un unico posto. I primi
          100 chef iscritti ricevono 30 giorni di Premium Silver gratis e il
          badge Founder permanente.
        </p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Button asChild className="bg-gold text-gold-foreground hover:bg-gold/90">
            <Link to="/diventa-chef">
              <ChefHat className="mr-1.5 h-4 w-4" /> Diventa Chef
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/contatti">Contattaci</Link>
          </Button>
        </div>
      </section>

      <p className="mt-14 border-t border-border pt-6 text-xs text-muted-foreground">
        Questa pagina è mantenuta dal team di ChefConnect Italia. Per domande
        specifiche scrivi a{" "}
        <a href="mailto:informazioni@chefconnectitalia.com" className="text-foreground hover:text-gold">
          informazioni@chefconnectitalia.com
        </a>
        .
      </p>
    </article>
  );
}